(function () {
  if (customElements.get('cart-pairs-with')) return;
  let suiteAssetsPromise;

  function loadSuiteAssets(element) {
    if (customElements.get('product-selection-suite')) return Promise.resolve();
    if (suiteAssetsPromise) return suiteAssetsPromise;
    suiteAssetsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = element.dataset.suiteScript;
      script.onload = () => customElements.get('product-selection-suite') ? resolve() : reject(new Error('Product options could not be loaded.'));
      script.onerror = () => reject(new Error('Product options could not be loaded.'));
      document.head.append(script);
    }).catch((error) => { suiteAssetsPromise = null; throw error; });
    return suiteAssetsPromise;
  }

  const sizeAssets = new Map();
  async function loadSizeAssets(content) {
    const tag = content.dataset.suiteTag;
    if (!customElements.get(tag)) {
      if (!sizeAssets.has(tag)) sizeAssets.set(tag, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = content.dataset.suiteScript;
        script.onload = () => customElements.get(tag) ? resolve() : reject(new Error('Product options unavailable.'));
        script.onerror = () => reject(new Error('Product options could not be loaded.'));
        document.head.append(script);
      }).catch((error) => { sizeAssets.delete(tag); throw error; }));
      await sizeAssets.get(tag);
    }
    return tag;
  }
  function registerPopupSuite(tag) {
    const popupTag = `cart-pairs-${tag}`;
    if (customElements.get(popupTag)) return popupTag;
    const Suite = customElements.get(tag);
    class CartPairsSelectionSuite extends Suite {
      money(cents) { return this.moneyFixed ? this.moneyFixed(cents) : super.money(cents); }
      setPopupStatus(message) { if (this.setStatus) this.setStatus(message); else this.status.textContent = message; }
      async addToCart() {
        if (this.isAdding || (this.isReady ? !this.isReady() : this.addButton.disabled)) return;
        this.isAdding = true;
        this.setPopupStatus('');
        this.render();
        const dialog = this.closest('dialog');
        dialog.querySelector('[data-pairs-close]').disabled = true;
        try {
          let formData;
          if (tag === 'salon-product-selection-suite' || tag === 'travel-product-selection-suite') {
            const variant = this.findSelectedVariant();
            const salon = tag === 'salon-product-selection-suite';
            formData = new FormData();
            formData.set('id', variant.id);
            formData.set('quantity', this.isLowMoq() ? this.state.exactQuantity : 1);
            formData.set('properties[Label Printing]', this.state.printing === 'yes' ? this.getPrintingPackLabel() : 'No');
            formData.set('properties[Label Application]', salon
              ? (this.state.printing === 'yes' ? 'Yes, complimentary' : 'No')
              : (this.state.application === 'yes' ? this.getApplicationPackLabel() : 'No'));
            const supply = this.state.supply === 'standard' ? 'Standard Pro Salon Labels'
              : this.state.supply === 'file' ? 'Use label file on record'
              : this.state.supply === 'upload' ? 'New label file attached' : '';
            if (supply) formData.set('properties[Label Supply]', supply);
            if (this.state.supply === 'upload') {
              const file = this.fileInputs[0]?.files?.[0];
              formData.set('properties[Shopify Label URL]', file, file.name);
            }
          } else if (this.findThemeControls) {
            this.findThemeControls();
            this.variant = this.findSelectedVariant();
            this.applySelectedVariantToForm();
            this.syncLineItemProperties();
            formData = this.state.supply === 'upload'
              ? this.buildFileUploadFormData() : new FormData(this.nativeForm);
          } else {
            formData = new FormData();
            formData.set('id', this.variant.id);
            formData.set('quantity', tag === 'sample-product-selection-suite' ? this.quantity : 1);
          }
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData,
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'This product could not be added. Please try again.');
          // The modal owns its form, so no product-page form or legacy app can
          // replace the upsell's variant, quantity or label properties.
          this.dispatchEvent(new CustomEvent('cart-pairs:added', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          document.querySelector('cart-drawer')?.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
        } catch (error) {
          this.setPopupStatus(error.message || 'This product could not be added. Please try again.');
        } finally {
          this.isAdding = false;
          dialog.querySelector('[data-pairs-close]').disabled = false;
          if (this.isConnected) this.render();
        }
      }
    }
    customElements.define(popupTag, CartPairsSelectionSuite);
    return popupTag;
  }

  class CartPairsWith extends HTMLElement {
    connectedCallback() {
      this.generation = 0;
      this.refreshHandler = () => this.refresh();
      this.clickHandler = (event) => {
        const button = event.target.closest('[data-pairs-open]');
        if (button && !button.disabled) this.openProduct(button);
      };
      this.addEventListener('click', this.clickHandler);
      document.addEventListener('theme:cart:change', this.refreshHandler);
      this.refresh();
    }
    disconnectedCallback() {
      this.generation += 1;
      document.removeEventListener('theme:cart:change', this.refreshHandler);
      this.removeEventListener('click', this.clickHandler);
      this.closeProduct();
    }
    rootUrl(path) {
      return `${this.dataset.rootUrl.replace(/\/$/, '')}/${path}`;
    }
    async refresh() {
      const generation = ++this.generation;
      try {
        const cartResponse = await fetch(this.rootUrl('cart.js'), { cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const cart = await cartResponse.json();
        if (generation !== this.generation || !this.isConnected) return;
        const key = cart.items.map((item) => `${item.product_id}:${item.variant_id}:${item.quantity}`).sort().join('|');
        if (key === this.cartKey) return;
        const triggersUrl = new URL(this.dataset.rootUrl, window.location.origin);
        triggersUrl.searchParams.set('section_id', 'api-cart-pairs-triggers');
        const triggersResponse = await fetch(triggersUrl, { cache: 'no-store' });
        if (!triggersResponse.ok) throw new Error('Product configuration unavailable');
        const triggersDoc = new DOMParser().parseFromString(await triggersResponse.text(), 'text/html');
        const triggers = Array.from(triggersDoc.querySelectorAll('[data-pairs-trigger]'));
        let selectedCard = null;
        for (const trigger of triggers) {
          const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
          url.searchParams.set('product_id', trigger.dataset.pairsTrigger);
          url.searchParams.set('intent', 'complementary');
          url.searchParams.set('limit', '10');
          url.searchParams.set('section_id', 'api-cart-pairs-with');
          const response = await fetch(url, { cache: 'no-store' });
          if (!response.ok) continue;
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = parsed.querySelector('[data-paired-product]');
          if (card && !cart.items.some((item) => String(item.product_id) === card.dataset.pairedProduct)) {
            card.querySelector('[data-pairs-message]').textContent = trigger.dataset.message;
            selectedCard = card;
            break;
          }
        }
        if (generation !== this.generation || !this.isConnected) return;
        this.replaceChildren();
        this.hidden = !selectedCard;
        if (selectedCard) this.append(document.importNode(selectedCard, true));
        if (!triggers.length && !this.dialog?.querySelector('[data-pairs-suite]')?.isAdding) this.closeProduct();
        this.cartKey = key;
      } catch {
        if (generation === this.generation) { this.hidden = true; this.cartKey = ''; }
      }
    }
    async openProduct(button) {
      button.disabled = true;
      const status = this.querySelector('[data-pairs-status]');
      status.textContent = 'Loading product options…';
      try {
        const url = new URL(button.dataset.productUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Product options unavailable.');
        url.searchParams.set('view', 'cart-pairs-suite');
        const [response] = await Promise.all([fetch(url), loadSuiteAssets(this)]);
        if (!response.ok) throw new Error('Product options could not be loaded. Please try again.');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = parsed.querySelector('[data-cart-pairs-product]');
        if (!content) throw new Error('This product’s options are unavailable.');
        // Detach before upgrading: a DOMParser document is connected too.
        // Initialising there would copy the ready marker without its listeners.
        content.remove();
        const suiteTag = await loadSizeAssets(content);
        const popupTag = registerPopupSuite(suiteTag);
        const styleUrl = new URL(this.dataset.suiteStyle, window.location.origin).href;
        if (![...document.querySelectorAll('link[rel="stylesheet"]')].some((link) => link.href === styleUrl)) {
          const style = document.createElement('link');
          style.rel = 'stylesheet'; style.href = styleUrl; document.head.append(style);
        }
        // Reuse the server-rendered suite with a fresh, isolated custom element.
        const sizeStyle = content.dataset.suiteStyle;
        if (suiteTag !== 'product-selection-suite' && suiteTag !== 'bulk-product-selection-suite' && !Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some((link) => link.href === sizeStyle)) {
          const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = sizeStyle; document.head.append(link);
        }
        const original = content.querySelector(suiteTag);
        if (!original) throw new Error('Product options are unavailable.');
        const suite = document.createElement(popupTag);
        for (const attribute of original.attributes) suite.setAttribute(attribute.name, attribute.value);
        suite.removeAttribute('data-pss-initialised');
        suite.setAttribute('data-pairs-suite', '');
        suite.append(...Array.from(original.childNodes));
        original.replaceWith(suite);
        content.querySelectorAll('script:not([type="application/json"]), link[rel="stylesheet"]').forEach((node) => node.remove());
        this.opener = button;
        const dialog = document.createElement('dialog');
        dialog.className = 'cart-pairs-modal';
        dialog.setAttribute('data-scroll-lock-scrollable', '');
        dialog.setAttribute('aria-labelledby', 'CartPairsProductTitle');
        dialog.setAttribute('aria-modal', 'true');
        dialog.style.setProperty('--pairs-cart-width', `${Math.round(this.closest('cart-drawer').querySelector('.drawer__inner').getBoundingClientRect().width)}px`);
        const close = document.createElement('button');
        close.type = 'button'; close.className = 'cart-pairs-modal__close'; close.dataset.pairsClose = '';
        close.setAttribute('aria-label', 'Close product options'); close.textContent = '×';
        close.addEventListener('click', () => this.closeProduct());
        dialog.append(close, document.importNode(content, true));
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault(); event.stopPropagation();
          // The theme closes drawers on Escape keyup. Consume this release
          // after removing the dialog so only the product popup is dismissed.
          const stopEscapeRelease = (keyEvent) => {
            if (keyEvent.key !== 'Escape') return;
            keyEvent.stopImmediatePropagation();
            document.removeEventListener('keyup', stopEscapeRelease, true);
          };
          document.addEventListener('keyup', stopEscapeRelease, true);
          window.setTimeout(() => document.removeEventListener('keyup', stopEscapeRelease, true), 1000);
          if (!dialog.querySelector('[data-pairs-suite]')?.isAdding) this.closeProduct();
        });
        dialog.addEventListener('keydown', (event) => { if (event.key === 'Escape') event.stopPropagation(); });
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if ((event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) && !dialog.querySelector('[data-pairs-suite]')?.isAdding) this.closeProduct();
        });
        dialog.addEventListener('cart-pairs:added', () => this.closeProduct());
        this.dialog = dialog;
        document.body.append(dialog);
        dialog.showModal();
        status.textContent = '';
      } catch (error) {
        status.textContent = error.message || 'Product options could not be loaded. Please try again.';
      } finally { button.disabled = false; }
    }
    closeProduct() {
      if (!this.dialog) return;
      const dialog = this.dialog;
      this.dialog = null;
      dialog.close(); dialog.remove();
      const focusTarget = this.opener?.isConnected ? this.opener : document.querySelector('[data-cart-drawer-close]');
      focusTarget?.focus();
      if (window.a11y) window.a11y.lastElement = focusTarget;
    }
  }
  customElements.define('cart-pairs-with', CartPairsWith);
})();
